<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="maxWidth"
    :fullscreen="fullscreenComputed"
    :persistent="persistent || mandatory"
    :scroll-strategy="scrollStrategy"
    scrollable
    :transition="fullscreenComputed ? 'dialog-bottom-transition' : undefined"
    @update:model-value="handleModelUpdate"
  >
    <v-card class="gg-dialog" :class="{ 'gg-dialog--fullscreen': fullscreenComputed }">
      <!-- Header -->
      <v-card-title class="gg-dialog__header">
        <span class="gg-dialog__title">{{ title }}</span>
        <gg-btn-icon v-if="!mandatory" icon="ms:close" @click="handleClose" />
      </v-card-title>

      <!-- Content -->
      <v-card-text class="gg-dialog__content">
        <slot />
      </v-card-text>

      <!-- Prepend Actions (e.g. missing fields bar) -->
      <div v-if="hasPrependActionsSlot" class="gg-dialog__prepend-actions">
        <slot name="prepend-actions" />
      </div>

      <!-- Actions -->
      <v-card-actions v-if="hasActionsSlot || confirmText" class="gg-dialog__actions">
        <slot name="actions">
          <!-- Default actions layout -->
          <gg-btn
            v-if="cancel"
            variant="outlined"
            @click="handleCancel"
          >
            {{ cancelText }}
          </gg-btn>

          <v-spacer />

          <gg-btn
            color="primary"
            :loading="loading"
            :disabled="loading || disabled || confirmDisabled"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </gg-btn>
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
/**
 * GgDialog — Standardized Dialog Component
 *
 * @version 1.1.0
 * @description Branded dialog with consistent header (title + close), content slot,
 *              and optional action bar (confirm/cancel buttons or custom slot).
 *
 * @changelog v1.1.0: `scroll-strategy="none"` when fullscreen to fix iOS Safari
 *   PWA bug where `env(safe-area-inset-bottom)` collapsed to 0 momentarily
 *   during modal open/close, leaving `.mobile-bottom-fixed` shifted upward
 *   with a visible white sliver below the branded bar until the user scrolled.
 *
 * @example Basic usage
 * ```vue
 * <gg-dialog v-model="open" title="Crea utente" confirm-text="Salva" @confirm="save">
 *   <p>Form content here</p>
 * </gg-dialog>
 * ```
 *
 * @example With cancel + custom actions
 * ```vue
 * <gg-dialog v-model="open" title="Conferma" confirm-text="OK" cancel @cancel="reset">
 *   <p>Sei sicuro?</p>
 * </gg-dialog>
 * ```
 *
 * @example Fully custom actions
 * ```vue
 * <gg-dialog v-model="open" title="Custom">
 *   <p>Body</p>
 *   <template #actions>
 *     <gg-btn color="error" @click="remove">Elimina</gg-btn>
 *     <v-spacer />
 *     <gg-btn color="primary" @click="save">Salva</gg-btn>
 *   </template>
 * </gg-dialog>
 * ```
 */

import { computed, useSlots } from 'vue'
import { useResponsive } from '@/composables/useResponsive'

// =============================================================================
// Props
// =============================================================================

const props = withDefaults(defineProps<{
  /** v-model — dialog open/close state */
  modelValue: boolean
  /** Header title (left-aligned) */
  title?: string
  /** Confirm button text. If empty, default actions bar is hidden (unless #actions slot is used) */
  confirmText?: string
  /** Show cancel button on the left */
  cancel?: boolean
  /** Cancel button text override */
  cancelText?: string
  /** Disables confirm button and shows spinner */
  loading?: boolean
  /** v-dialog max-width */
  maxWidth?: string | number
  /** v-dialog persistent (no close on outside click) */
  persistent?: boolean
  /** Hides close (X) button — forces user to respond via action buttons */
  mandatory?: boolean
  /** Disables the entire dialog card (loading overlay) */
  disabled?: boolean
  /** Disables only the confirm button (e.g. form validation incomplete) */
  confirmDisabled?: boolean
  /** Force fullscreen regardless of breakpoint */
  fullscreen?: boolean
  /** Disable auto-fullscreen on mobile (default: true = auto-fullscreen on mobile) */
  noAutoFullscreen?: boolean
  /**
   * Explicit override for the actions wrapper rendering.
   *
   * Workaround for the Vue 3 quirk where `v-if` on `<template #actions>` does
   * NOT prevent the slot from being registered: `slots.actions` stays truthy
   * (it's a render function), so slot-presence detection alone gives a false
   * positive and the wrapper renders empty.
   *
   * - `undefined` (default) → legacy behaviour: render the wrapper if a slot is
   *   provided OR `confirmText` is set.
   * - `true`  → force-render the wrapper (even if no slot/confirmText).
   * - `false` → force-hide the wrapper (even if a slot is registered).
   *
   * Use this when the parent decides at runtime whether to render the actions
   * (e.g. `:show-actions="isMobileOrTablet"`).
   */
  showActions?: boolean
  /**
   * Explicit override for the prepend-actions wrapper rendering.
   *
   * Same Vue 3 slot-detection quirk as `showActions`. Use this prop to bypass
   * `slots['prepend-actions']` detection when the parent controls the wrapper
   * via a runtime condition (e.g. `:show-prepend-actions="hasMissingFields"`).
   *
   * @default undefined — falls back to slot-presence detection.
   */
  showPrependActions?: boolean
}>(), {
  title: '',
  confirmText: '',
  cancel: false,
  cancelText: 'Annulla',
  loading: false,
  maxWidth: 600,
  persistent: false,
  mandatory: false,
  disabled: false,
  confirmDisabled: false,
  fullscreen: false,
  noAutoFullscreen: false,
  showActions: undefined,
  showPrependActions: undefined,
})

// =============================================================================
// Emits
// =============================================================================

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: []
  cancel: []
}>()

// =============================================================================
// Slots detection
// =============================================================================
//
// IMPORTANT: `slots.<name>` in Vue 3 is a render function and stays truthy
// even when the parent uses `v-if` on `<template #slot>` (the function returns
// an empty array but the key is still registered). To allow callers to opt out
// of the wrapper rendering at runtime, we expose explicit `showActions` /
// `showPrependActions` props that take precedence over slot-presence detection.

const slots = useSlots()
const { isMobileOrTablet } = useResponsive()

const hasActionsSlot = computed(() => {
  // Explicit override (supports runtime conditions like `:show-actions="isMobile"`)
  if (props.showActions !== undefined) return props.showActions
  // Fallback: legacy slot-presence detection
  return !!slots.actions
})

const hasPrependActionsSlot = computed(() => {
  if (props.showPrependActions !== undefined) return props.showPrependActions
  return !!slots['prepend-actions']
})

/** Auto-fullscreen on mobile unless explicitly disabled */
const fullscreenComputed = computed(() => {
  if (props.fullscreen) return true
  if (props.noAutoFullscreen) return false
  return isMobileOrTablet.value
})

/**
 * Scroll-strategy override (v1.1.0 — FE v2.67.12 fix).
 *
 * BUG: Vuetify default `scroll-strategy="block"` applies `position: fixed`
 * to `<html>` to lock body scroll while a dialog is open. On iOS Safari in
 * PWA standalone mode, this momentarily causes `env(safe-area-inset-bottom)`
 * to be recalculated to 0. When the dialog closes, the env value stays
 * stale until the user scrolls, which forces a layout recompute. Symptom:
 * `padding-bottom: env(safe-area-inset-bottom)` on `AppMobileBrandedBar`
 * collapses, the colored footer no longer extends into the home indicator
 * area, white sliver becomes visible below the orange bar.
 *
 * FIX: when the dialog is fullscreen, the body is completely hidden by the
 * dialog anyway — there is NO need to lock body scroll. Use `none` to
 * sidestep the iOS Safari quirk entirely. For non-fullscreen dialogs, keep
 * the default `block` (the user might see/touch the body behind the scrim).
 */
const scrollStrategy = computed<'block' | 'none'>(() =>
  fullscreenComputed.value ? 'none' : 'block'
)

// =============================================================================
// Handlers
// =============================================================================

function handleModelUpdate(value: boolean) {
  if (!value && props.mandatory) return
  emit('update:modelValue', value)
  if (!value) emit('cancel')
}

function handleClose() {
  emit('update:modelValue', false)
  emit('cancel')
}

function handleCancel() {
  emit('update:modelValue', false)
  emit('cancel')
}

function handleConfirm() {
  emit('confirm')
}
</script>

<style lang="scss" scoped>
@use '@/themes/variables' as *;

.gg-dialog {
  border-radius: $border-radius-lg !important;
  overflow: hidden;

  // ───────────────────────────────────────────────────────────────────────────
  // Header
  // ───────────────────────────────────────────────────────────────────────────

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $spacing-md $spacing-md $spacing-md $spacing-lg;
    min-height: 56px;
  }

  &__title {
    font-size: $font-size-xxl;
    font-weight: $font-weight-normal;
    color: rgb(var(--v-theme-on-surface));
    line-height: 1.3;
    // Ellipsis for long titles
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1 1 0;
    margin-right: $spacing-sm;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Content
  // ───────────────────────────────────────────────────────────────────────────

  &__content {
    padding: $spacing-lg !important;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Prepend Actions (bar above action buttons)
  // ───────────────────────────────────────────────────────────────────────────

  &__prepend-actions {
    flex-shrink: 0;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Actions
  // ───────────────────────────────────────────────────────────────────────────

  &__actions {
    padding: $spacing-md $spacing-lg !important;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Fullscreen variant (mobile)
  // ───────────────────────────────────────────────────────────────────────────

  &--fullscreen {
    border-radius: 0 !important;
    display: flex;
    flex-direction: column;
    height: 100%;

    .gg-dialog__header {
      flex-shrink: 0;
      padding: calc(#{$spacing-sm} + env(safe-area-inset-top, 0px)) $spacing-md $spacing-sm $spacing-md;
      border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
      min-height: 48px;
    }

    .gg-dialog__title {
      font-size: $font-size-lg;
    }

    .gg-dialog__content {
      flex: 1;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      padding: $spacing-md !important;

      // Kill nested v-container padding (many dialogs nest containers)
      :deep(.v-container) {
        padding: $spacing-xs 0;
      }

      // Section titles inside dialogs: compact
      :deep(.section-title) {
        font-size: 14px;
        margin-bottom: $spacing-xs !important;
        margin-top: 0 !important;
        padding-top: $spacing-xs;
        border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));

        &:first-child {
          border-top: none;
          padding-top: 0;
        }
      }

      // Field labels inside dialogs: smaller
      :deep(.field-label) {
        font-size: 13px;
        min-width: 75px;
        flex-shrink: 0;
      }
    }

    .gg-dialog__actions {
      flex-shrink: 0;
      border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
      padding: $spacing-sm $spacing-md calc(#{$spacing-sm} + env(safe-area-inset-bottom, 0px)) $spacing-md !important;
    }

    // Touch target enforcement (WCAG 2.5.5 — 44px minimum)
    :deep(.v-btn-toggle .v-btn) {
      min-height: 44px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .gg-dialog__actions .v-btn { min-height: 44px; }
    :deep(.v-text-field:not(.v-textarea) .v-field__input),
    :deep(.v-select .v-field__input),
    :deep(.v-autocomplete .v-field__input) {
      min-height: 44px;
      padding-top: 0;
      padding-bottom: 0;
      display: flex;
      align-items: center;
    }

    // Button toggles: full width, equal sizing, ellipsis
    :deep(.v-btn-toggle) {
      width: 100%;
      max-width: 100%;
      min-height: 44px;

      .v-btn {
        flex: 1 1 0;
        min-width: 0 !important;
        max-width: none;

        .v-btn__content {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
          max-width: 100%;
        }
      }
    }
  }
}
/* =============================================================================
   v-switch has automatic opacity -> force it to 1 so it doesn't look disabled when using custom colors
   ============================================================================= */
:deep(.v-switch__track) {
  opacity: 1 !important;
}
</style>
